import { Box, Breadcrumb, Flex, Pagination } from '@cowsquare/design';
import { Link } from 'gatsby';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React from 'react';
import { Query } from 'react-apollo';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { space } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { dateFormat } from '../../../../utils/dateFormat';
import { getHTMLString } from '../../../../utils/getHTMLString';
import { getQueryObject } from '../../../../utils/getQueryObject';
import { usePagesBindingUrl } from '../../../../utils/hooks';
import { BreadcrumbWrapper } from '../../../BreadcrumbWrapper';
import { Section } from '../../../Section';
import ZoomImage from '../../../ZoomImage';

const PAGE_SIZE = 20;

const Name = styled(Box).attrs({
  mb: 4,
})`
  font-size: 24px;
  font-weight: normal;
  line-height: 1.42;
`;

const DateAndLocation = styled.div`
  font-size: 13px;
  line-height: 1.54;
  color: ${themeGet('photo.dateAndLocation.color')};
`;

const Desc = styled(Box).attrs({
  mt: 6,
})`
  font-size: 14px;
  line-height: 1.43;
  color: ${themeGet('photo.desc.color')};
`;

const GalleryFlex = styled(Flex).attrs({
  flexWrap: 'wrap',
  justifyContent: ['space-between', 'flex-start'],
  mx: [0, '-8px'],
  mb: 8,
})`
  width: 100%;
  ${space}
`;

const GalleryBox = styled(Box).attrs({
  width: ['calc(50% - 1px)', 1 / 4],
  px: [0, 4],
  pb: [2, 5],
})`
  display: inline-block;
`;

const GET_MEMBERS_ALBUMS_BY_PK = gql`
  query hkdutest_members_albums_by_pk($id: uuid!, $limit: Int, $offset: Int) {
    total: hkdutest_members_photos_aggregate(where: { album_id: { _eq: $id } }) {
      aggregate {
        count
      }
    }
    list: hkdutest_members_albums_by_pk(id: $id) {
      id
      name
      date
      location
      description
      photos(limit: $limit, offset: $offset, order_by: { date: desc }) {
        file {
          link
        }
      }
    }
  }
`;

const Photo = ({ id, uri, location }) => {
  const queryObject = getQueryObject(location);
  const [page, onPageChange] = usePagesBindingUrl(queryObject, uri);

  return (
    <Query
      query={GET_MEMBERS_ALBUMS_BY_PK}
      variables={{ id, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }}
    >
      {({ data }) => {
        const album = R.pathOr({}, ['list'], data);
        const title = R.propOr('', 'name', album);
        const photos = R.pathOr([], ['photos'], album);
        const description = R.pipe(R.propOr('', 'description'), getHTMLString)(album);
        const total = R.pathOr(0, ['total', 'aggregate', 'count'], data);
        return (
          <Section>
            <Helmet>
              <title>{title} - HKDU Members Only</title>
            </Helmet>
            <BreadcrumbWrapper>
              <Breadcrumb>
                <Breadcrumb.Item linkProps={{ as: Link, to: '/member-only/album' }}>
                  Union Album
                </Breadcrumb.Item>
                <Breadcrumb.Item>{title}</Breadcrumb.Item>
              </Breadcrumb>
            </BreadcrumbWrapper>
            <Box mx={[5, 0]} mt={8} mb={6}>
              <Name>{title}</Name>
              <DateAndLocation>
                {dateFormat(album.date || new Date().toISOString())}{' '}
                {album.location && `at ${album.location}`}
              </DateAndLocation>
              <Desc>{description}</Desc>
            </Box>
            <GalleryFlex>
              {photos.map(photo => (
                <GalleryBox key={photo.file.link}>
                  <ZoomImage src={photo.file.link} />
                </GalleryBox>
              ))}
            </GalleryFlex>
            {total > PAGE_SIZE && (
              <Flex justifyContent="center">
                <Box mb={8}>
                  <Pagination total={total} pageSize={PAGE_SIZE} current={page} onChange={onPageChange} />
                </Box>
              </Flex>
            )}
          </Section>
        );
      }}
    </Query>
  );
};
export default Photo;
